import React, { useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from '../UserContext';
import { generateImageWithDalle, generateImageWithDalle2, isDalleAvailable } from '../utils/dalleApi';
import { generateImageWithStableDiffusion, generateMoviePoster, isStableDiffusionAvailable } from '../utils/stableDiffusionApi';
import { generateImageWithGrok, isGrokAvailable } from '../utils/grokImageApi';
import { useNavigate } from 'react-router-dom';
import { useClaudeChat } from "../hooks/useClaudeChat";
import '../styles/components/ClaudeTest.scss';
import ClaudeMessageModal from './ClaudeMessageModal';
import ClaudeTester from './ClaudeTester';

const PASSWORD = "Luni&Irmoune";

const ENDPOINT_LABELS = {
    local: "Local proxy",
    cloud: "Cloudflare Worker",
    remote: "Remote API",
};

function formatEndpointLabel(endpoint, fallback = "Autodetect") {
    if (!endpoint) return fallback;
    const { kind, label, base } = endpoint;
    if (label) {
        if (/local/i.test(label)) return "Local proxy";
        if (/cloud/i.test(label) || /worker/i.test(label)) return "Cloudflare Worker";
        return label;
    }
    if (ENDPOINT_LABELS[kind]) {
        return ENDPOINT_LABELS[kind];
    }
    if (base) {
        try {
            const url = new URL(base);
            return url.hostname;
        } catch (_) {
            return base;
        }
    }
    return fallback;
}

function resolveFallback(attempts = []) {
    if (!Array.isArray(attempts) || attempts.length <= 1) return null;
    const firstFailure = attempts.find((item) => item && item.ok === false);
    if (!firstFailure) return null;
    const success = [...attempts].reverse().find((item) => item && item.ok);
    if (!success) return null;
    if (firstFailure.endpoint && success.endpoint && firstFailure.endpoint.base === success.endpoint.base) {
        return null;
    }
    return { from: firstFailure.endpoint, to: success.endpoint };
}

function pickPrimaryImageData(files = []) {
    for (const file of files) {
        if (!file) continue;
        if (file.type?.startsWith('image/')) {
            if (typeof file.data === 'string') return file.data;
            if (typeof file.preview === 'string') return file.preview;
        }
    }
    const withData = files.find((file) => typeof file?.data === 'string');
    return withData?.data || null;
}

const MODEL_LABELS = {
    'dalle3': 'DALL·E 3',
    'dalle2': 'DALL·E 2',
    'grok': 'Grok (xAI)',
    'stable-diffusion': 'Stable Diffusion',
    'stable-diffusion-movie': 'Stable Diffusion (Affiche)',
};

const MODEL_BASE_ORDER = ['dalle3', 'dalle2', 'grok', 'stable-diffusion', 'stable-diffusion-movie'];
const ASPECT_RATIOS = [
    {
        key: '1:1',
        label: '1:1 Carré',
        description: 'Format polyvalent',
        dalleSize: '1024x1024',
        width: 1024,
        height: 1024,
    },
    {
        key: '16:9',
        label: '16:9 Paysage',
        description: 'Bannière / écran large',
        dalleSize: '1792x1024',
        width: 1024,
        height: 576,
    },
    {
        key: '9:16',
        label: '9:16 Portrait',
        description: 'Story / affiche verticale',
        dalleSize: '1024x1792',
        width: 576,
        height: 1024,
    },
];
const STABLE_DIFFUSION_RATIO_SIZES = {
    '1:1': { width: 1024, height: 1024 },
    '16:9': { width: 1344, height: 768 },
    '9:16': { width: 768, height: 1344 },
};
const MODEL_OPTION_DEFINITIONS = [
    {
        key: 'dalle3',
        title: 'DALL-E 3',
        description: 'Haute qualité (OpenAI)',
    },
    {
        key: 'dalle2',
        title: 'DALL-E 2',
        description: 'Plus rapide / moins coûteux',
    },
    {
        key: 'grok',
        title: 'Grok (xAI)',
        description: 'Ultra HQ (~0,30 $ / image)',
    },
    {
        key: 'stable-diffusion',
        title: 'Stable Diffusion',
        description: '512-1024px, crédits Stability',
    },
    {
        key: 'stable-diffusion-movie',
        title: 'Stable Diffusion (Affiche)',
        description: 'Preset cinéma / style dramatique',
    },
];

function getDefaultImageModel() {
    if (isDalleAvailable()) return 'dalle3';
    if (isGrokAvailable()) return 'grok';
    if (isStableDiffusionAvailable()) return 'stable-diffusion';
    return MODEL_BASE_ORDER[0];
}

function getRatioConfig(key) {
    return ASPECT_RATIOS.find((ratio) => ratio.key === key) || ASPECT_RATIOS[0];
}

const PROVIDER_HINTS = {
    'dalle3': 'Ajoute REACT_APP_OPENAI_API_KEY puis relance npm start.',
    'dalle2': 'Ajoute REACT_APP_OPENAI_API_KEY puis relance npm start.',
    'grok': 'Ajoute REACT_APP_GROK_API_KEY (clé xAI) puis relance npm start.',
    'stable-diffusion': 'Ajoute REACT_APP_STABLE_DIFFUSION_API_KEY (Stability).',
    'stable-diffusion-movie': 'Ajoute REACT_APP_STABLE_DIFFUSION_API_KEY (Stability).',
};

export default function ClaudeTest() {
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const [authenticated, setAuthenticated] = useState(false);
    const [input, setInput] = useState("");
    const [isHovering, setIsHovering] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    
    // Ces hooks doivent être déclarés avant tout rendu conditionnelnpm funfg
    const [userMessage, setUserMessage] = useState('');
    const [conversation, setConversation] = useState([]);
    const {
        sendMessage,
        streamMessage,
        abort: abortClaude,
        isStreaming,
        isSending,
        isBusy: isClaudeBusy,
        activeEndpoint,
    } = useClaudeChat();
    const [isConnected, setIsConnected] = useState(true);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [modalMessage, setModalMessage] = useState(null);
    const [generatingImage, setGeneratingImage] = useState(false);
    const [generatedImage, setGeneratedImage] = useState(null);
    const [imagePrompt, setImagePrompt] = useState('');
    const [showImageModal, setShowImageModal] = useState(false);
    const [autoFallbackEnabled, setAutoFallbackEnabled] = useState(true);
    const [selectedModel, setSelectedModel] = useState(() => getDefaultImageModel());
    const [selectedRatio, setSelectedRatio] = useState(ASPECT_RATIOS[0].key);
    const [providerStatus, setProviderStatus] = useState({});
    const providerAvailability = {
        'dalle3': isDalleAvailable,
        'dalle2': isDalleAvailable,
        'grok': isGrokAvailable,
        'stable-diffusion': isStableDiffusionAvailable,
        'stable-diffusion-movie': isStableDiffusionAvailable,
    };
    const updateProviderStatus = (key, status, reason = null) => {
        if (!key) return;
        setProviderStatus(prev => ({
            ...prev,
            [key]: { status, reason, updatedAt: Date.now() }
        }));
    };
    const isModelSelectable = (key) => {
        const checker = providerAvailability[key];
        const available = checker ? checker() : false;
        if (!available) return false;
        const state = providerStatus[key]?.status;
        return state !== 'blocked' && state !== 'missing';
    };

    useEffect(() => {
        if (isModelSelectable(selectedModel)) return;
        const fallback = MODEL_BASE_ORDER.find((key) => isModelSelectable(key));
        if (fallback && fallback !== selectedModel) {
            setSelectedModel(fallback);
        }
    }, [selectedModel, providerStatus]);
    // Contexte d'exécution / badge source API
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalhost = ['localhost', '127.0.0.1'].includes(hostname) || /^192\.168\./.test(hostname);
    const defaultSourceLabel = isLocalhost ? 'Local proxy' : 'Cloudflare Worker';
    const apiSourceLabel = formatEndpointLabel(activeEndpoint, defaultSourceLabel);
    const apiSourceTooltip = activeEndpoint?.base
        ? `${apiSourceLabel} • ${activeEndpoint.base}`
        : `${apiSourceLabel} • auto`;
    // Options d'envoi des pièces jointes
    const [includeFilesNextSend, setIncludeFilesNextSend] = useState(true);
    const [keepFilesAfterSend, setKeepFilesAfterSend] = useState(false);
    
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const streamErrorHandledRef = useRef(false);
    const [chat, setChat] = useState([]); // [{ who:'user'|'assistant'|'system', text:string }]
    const chatBusy = isClaudeBusy;
    const showTypingIndicator = isSending && !isStreaming;
    const [showPassword, setShowPassword] = useState(false);  

    // Scroll auto en bas
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [conversation]);

    // Chargement messages (Désactivé pour tests Claude)
    useEffect(() => {
        if (!user?.uid) return;
        const loadMessages = async () => {
            try {
                setIsConnected(true);
                setConversation([]);
            } catch (error) {
                setIsConnected(false);
            }
        };
        loadMessages();
    }, [user]);

    // Alerte variables d'environnement manquantes en DEV
    useEffect(() => {
        if (!isLocalhost) return;
        const missing = [];
        if (!process.env.REACT_APP_CLAUDE_API_KEY) missing.push('REACT_APP_CLAUDE_API_KEY');
        if (!process.env.REACT_APP_OPENAI_API_KEY) missing.push('REACT_APP_OPENAI_API_KEY');
        if (!process.env.REACT_APP_STABLE_DIFFUSION_API_KEY) missing.push('REACT_APP_STABLE_DIFFUSION_API_KEY');
        if (missing.length > 0) {
            setConversation(prev => [...prev, {
                id: `env-missing-${Date.now()}`,
                role: 'system',
                content: `⚠️ Variables manquantes en DEV: ${missing.join(', ')}. Ajoute-les dans .env.local puis relance npm start.`,
                timestamp: new Date()
            }]);
        }
    }, [isLocalhost]);

    // ---------------- AUTHENTIFICATION PAR MOT DE PASSE ----------------
    if (!authenticated) {
        return (
            <div
            style={{
                backgroundImage: `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundAttachment: "fixed",
                backgroundBlendMode: "overlay",
                backgroundColor: "#200211",
                opacity: "0.92",
                minHeight: "100vh",
                minWidth: "100vw",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                position: "fixed",
                top: 0,
                left: 0,
                zIndex: 1000,
            }}
            >
            <div
                style={{
                background: "rgba(20,10,35,0.94)",
                borderRadius: 20,
                padding: "50px 40px",
                boxShadow: "0 6px 32px #0009",
                textAlign: "center",
                maxWidth: 520,
                width: "92vw",
                }}
            >
                <h2 style={{ color: "#00FFFF" }}>🔒 Accès réservé à l'assistant Claude</h2>
                <p style={{ color: "white", marginBottom: 24 }}>
                Entrez le mot de passe transmis par Phil pour débloquer cette fonctionnalité :
                </p>

                {/* Champ mot de passe + icône visibilité */}
                <div style={{ position: "relative", display: "inline-block" }}>
                <input
                    type={showPassword ? "text" : "password"}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    style={{
                    fontSize: 20,
                    padding: "12px 44px 12px 12px", // espace à droite pour l’icône
                    minWidth: 220,
                    borderRadius: 6,
                    border: "1px solid #00FFFF",
                    outline: "none",
                    marginBottom: 10,
                    color: "#fff",
                    background: "rgba(0,0,0,0.25)",
                    }}
                    placeholder="Mot de passe"
                    autoFocus
                    onKeyDown={(e) => {
                    if (e.key === "Enter") setAuthenticated(input === PASSWORD);
                    }}
                />

                {/* Bouton œil */}
                <button
                    type="button"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                    onClick={() => setShowPassword((v) => !v)}
                    style={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    width: 28,
                    height: 28,
                    borderRadius: 6,
                    border: "1px solid rgba(255,255,255,0.25)",
                    background: "rgba(255,255,255,0.06)",
                    color: "#00FFFF",
                    cursor: "pointer",
                    }}
                    title={showPassword ? "Masquer" : "Afficher"}
                >
                    {showPassword ? "🙈" : "👁️"}
                </button>
                </div>

                <button
                onClick={() => setAuthenticated(input === PASSWORD)}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                style={{
                    marginLeft: 10,
                    fontSize: 20,
                    padding: "10px 26px",
                    background: isHovering ? "#00FFFF" : "white",
                    color: isHovering ? "black" : "#200211",
                    borderRadius: 6,
                    border: "none",
                    cursor: "pointer",
                    fontWeight: "bold",
                    transition: "background .18s",
                }}
                >
                OK
                </button>

                {input && input !== PASSWORD && (
                <p style={{ color: "#D90093", marginTop: 10 }}>Mot de passe incorrect</p>
                )}

                <div style={{ marginTop: 35 }}>
                <button
                    onClick={() => navigate("/")}
                    style={{
                    background: "#00FFFF",
                    color: "black",
                    fontWeight: "bold",
                    fontSize: 18,
                    borderRadius: 6,
                    border: "none",
                    padding: "10px 28px",
                    cursor: "pointer",
                    boxShadow: "0 3px 12px #0004",
                    }}
                >
                    🏠 Retour à l'accueil
                </button>
                </div>
            </div>
            </div>
        );
    }

    // Redimensionnement image
    const resizeImage = (file, maxWidth = 1024, maxHeight = 1024) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new window.Image();
                img.onload = () => {
                    let width = img.width, height = img.height;
                    if (width > height) {
                        if (width > maxWidth) { height *= maxWidth / width; width = maxWidth; }
                    } else {
                        if (height > maxHeight) { width *= maxHeight / height; height = maxHeight; }
                    }
                    const canvas = document.createElement('canvas');
                    canvas.width = width; canvas.height = height;
                    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                    resolve(canvas.toDataURL(file.type, 0.8));
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    };

    // Upload fichiers
    const handleFileUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        const maxFiles = 5;
        const filesToUpload = files.slice(0, maxFiles);
        const filePromises = filesToUpload.map(async (file) => {
            let data = null, preview = null;
            if (file.type.startsWith('image/')) {
                data = await resizeImage(file);
                preview = data;
            } else {
                data = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(file);
                });
            }
            return { id: `file-${Date.now()}-${Math.random()}`, name: file.name, type: file.type, size: file.size, data, preview };
        });
        Promise.all(filePromises).then(uploadedFilesData => {
            setUploadedFiles(prev => [...prev, ...uploadedFilesData]);
            const fileNames = uploadedFilesData.map(f => f.name).join(', ');
            setConversation(prev => [...prev, {
                id: 'temp-upload', role: 'system',
                content: `📎 Fichiers ajoutés: ${fileNames}`,
                timestamp: new Date(),
                attachedFiles: uploadedFilesData // Stocker les fichiers dans le message
            }]);
        });
    };

    const removeFile = (fileId) => {
        setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    };

    // Effacer historique
    const clearHistory = async () => {
        try {
            abortClaude();
            // Effacer la conversation
            setConversation([]);
            // Effacer les fichiers uploadés
            setUploadedFiles([]);
            // Fermer le modal de confirmation
            setShowClearConfirm(false);
            // Afficher un message de confirmation temporaire
            setConversation([{
                id: 'temp-clear', role: 'system',
                content: '💫 Historique et fichiers effacés avec succès',
                timestamp: new Date()
            }]);
            // Effacer le message après 2 secondes
            setTimeout(() => setConversation([]), 2000);
        } catch (error) {
            console.error('Erreur lors de l\'effacement:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!userMessage.trim() || chatBusy) return;

        const prompt = userMessage.trim();
        setUserMessage('');

        const attachments = includeFilesNextSend ? [...uploadedFiles] : [];
        const imageDataURL = includeFilesNextSend ? pickPrimaryImageData(uploadedFiles) : null;
        const userEntry = {
            id: `msg-user-${Date.now()}`,
            role: 'user',
            content: prompt,
            timestamp: new Date()
        };
        if (attachments.length) {
            userEntry.attachedFiles = attachments;
        }
        setConversation(prev => [...prev, userEntry]);

        try {
            const result = await sendMessage({
                prompt,
                attachments,
                image: imageDataURL,
            });

            const text = result?.data?.text || '';
            const assistantEntry = {
                id: `msg-claude-${Date.now()}`,
                role: 'claude',
                content: text,
                timestamp: new Date(),
            };
            if (result?.data?.usage) {
                assistantEntry.usage = result.data.usage;
            }
            assistantEntry.endpoint = result?.endpoint;

            setConversation(prev => [...prev, assistantEntry]);

            const fallbackInfo = resolveFallback(result?.attempts);
            if (fallbackInfo) {
                setConversation(prev => [...prev, {
                    id: `system-fallback-${Date.now()}`,
                    role: 'system',
                    content: `⚠️ Bascule automatique vers ${formatEndpointLabel(fallbackInfo.to, 'source alternative')} (depuis ${formatEndpointLabel(fallbackInfo.from, 'source initiale')}).`,
                    timestamp: new Date()
                }]);
            }

            if (!keepFilesAfterSend) setUploadedFiles([]);
        } catch (error) {
            console.error('Erreur Claude:', error);
            setConversation(prev => [...prev, {
                id: `temp-error-${Date.now()}`,
                role: 'system',
                content: `❌ Erreur: ${error?.message || error}`,
                timestamp: new Date()
            }]);
        }
    };
    const handleSubmitStream = async (e) => {
        e?.preventDefault?.();
        if (!userMessage.trim() || chatBusy) return;

        const prompt = userMessage.trim();
        setUserMessage('');
        streamErrorHandledRef.current = false;

        const attachments = includeFilesNextSend ? [...uploadedFiles] : [];
        const imageDataURL = includeFilesNextSend ? pickPrimaryImageData(uploadedFiles) : null;

        const userMsgId = `u-${Date.now()}`;
        const assistantId = `c-${Date.now()}`;

        setConversation(prev => [...prev, {
            id: userMsgId,
            role: 'user',
            content: prompt,
            timestamp: new Date(),
            attachedFiles: attachments
        }]);

        setConversation(prev => [...prev, {
            id: assistantId,
            role: 'claude',
            content: '',
            timestamp: new Date()
        }]);

        try {
            const result = await streamMessage({
                prompt,
                attachments,
                image: imageDataURL,
            }, {
                onToken: (delta) => {
                    if (!delta) return;
                    setConversation(prev =>
                        prev.map(m => m.id === assistantId ? { ...m, content: (m.content || '') + delta } : m)
                    );
                },
                onUsage: (usage) => {
                    if (!usage) return;
                    setConversation(prev =>
                        prev.map(m => m.id === assistantId ? { ...m, usage } : m)
                    );
                },
                onDone: () => {
                    if (!keepFilesAfterSend) setUploadedFiles([]);
                },
                onError: (err) => {
                    streamErrorHandledRef.current = true;
                    setConversation(prev => prev.map(m =>
                        m.id === assistantId
                            ? { ...m, content: `${m.content || ''}\n\n❌ ${err?.message || err}` }
                            : m
                    ));
                },
            });

            const fallbackInfo = resolveFallback(result?.attempts);
            if (fallbackInfo) {
                setConversation(prev => [...prev, {
                    id: `system-fallback-${Date.now()}`,
                    role: 'system',
                    content: `⚠️ Bascule automatique vers ${formatEndpointLabel(fallbackInfo.to, 'source alternative')} (depuis ${formatEndpointLabel(fallbackInfo.from, 'source initiale')}).`,
                    timestamp: new Date()
                }]);
            }
        } catch (err) {
            if (err?.name === 'AbortError') {
                setConversation(prev => prev.map(m =>
                    m.id === assistantId
                        ? { ...m, content: `${m.content || ''}\n\n⏹️ Streaming interrompu.` }
                        : m
                ));
            } else if (!streamErrorHandledRef.current) {
                setConversation(prev => prev.map(m =>
                    m.id === assistantId
                        ? { ...m, content: `${m.content || ''}\n\n❌ ${err?.message || err}` }
                        : m
                ));
            }
        } finally {
            streamErrorHandledRef.current = false;
        }
    };

    const handleStopStreaming = () => {
        abortClaude();
    };
      
      
      

    // Formatage timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    // Génération d'image avec gestion multi-fournisseurs (DALL·E / Grok / Stable Diffusion)
    const handleGenerateImage = async (prompt, preferredModel = selectedModel) => {
        const trimmedPrompt = (prompt || '').trim();
        if (!trimmedPrompt || generatingImage) return;
        const ratioConfig = getRatioConfig(selectedRatio);
        const dalle3Size = ratioConfig?.dalleSize || '1024x1024';
        const stableRatioConfig = STABLE_DIFFUSION_RATIO_SIZES[selectedRatio] || STABLE_DIFFUSION_RATIO_SIZES['1:1'];
        const sdWidth = stableRatioConfig.width;
        const sdHeight = stableRatioConfig.height;

        const providerExecutors = {
            'dalle3': async () => {
                const r = await generateImageWithDalle(trimmedPrompt, dalle3Size);
                return { url: r.url, revisedPrompt: r.revisedPrompt || trimmedPrompt, provider: MODEL_LABELS['dalle3'] };
            },
            'dalle2': async () => {
                const r = await generateImageWithDalle2(trimmedPrompt);
                return { url: r.url, revisedPrompt: r.revisedPrompt || trimmedPrompt, provider: MODEL_LABELS['dalle2'] };
            },
            'grok': async () => {
                const r = await generateImageWithGrok(trimmedPrompt, { ratioKey: selectedRatio });
                return { url: r.url, revisedPrompt: r.revisedPrompt || trimmedPrompt, provider: MODEL_LABELS['grok'] };
            },
            'stable-diffusion': async () => {
                const r = await generateImageWithStableDiffusion(trimmedPrompt, 30, 7, sdWidth, sdHeight, 'photographic');
                return { url: r.url, revisedPrompt: r.revisedPrompt || trimmedPrompt, provider: MODEL_LABELS['stable-diffusion'] };
            },
            'stable-diffusion-movie': async () => {
                const r = await generateMoviePoster(trimmedPrompt, { width: sdWidth, height: sdHeight });
                return { url: r.url, revisedPrompt: r.revisedPrompt || trimmedPrompt, provider: MODEL_LABELS['stable-diffusion-movie'] };
            }
        };

        const buildSequence = (firstChoice) => {
            const order = [];
            const push = (key) => {
                if (!key || order.includes(key)) return;
                order.push(key);
            };
            push(firstChoice);
            if (firstChoice === 'stable-diffusion-movie') push('stable-diffusion');
            MODEL_BASE_ORDER.forEach(push);
            return order;
        };

        const notifyFallback = (key, reason, statusOverride) => {
            setConversation(prev => [...prev, {
                id: `temp-img-warn-${Date.now()}-${key}`,
                role: 'system',
                content: `⚠️ ${MODEL_LABELS[key] || 'Service'} indisponible (${reason}). Tentative d'une alternative...`,
                timestamp: new Date()
            }]);
            if (statusOverride) {
                updateProviderStatus(key, statusOverride.status, statusOverride.reason);
            }
        };

        const sequence = autoFallbackEnabled ? buildSequence(preferredModel) : [preferredModel];
        const describeErrorStatus = (key, message) => {
            const normalized = (message || '').toLowerCase();
            if (normalized.includes('hard limit') || normalized.includes('billing')) {
                return {
                    status: 'blocked',
                    reason: 'Indisponible (limite de facturation atteinte – attendre le renouvellement ou recharger).'
                };
            }
            if (normalized.includes('insufficient') || normalized.includes('balance') || normalized.includes('quota')) {
                return {
                    status: 'blocked',
                    reason: 'Crédits insuffisants pour ce service. Recharge requise.'
                };
            }
            return { status: 'error', reason: message };
        };

        setGeneratingImage(true);
        try {
            let result = null;
            let lastErr = null;

            for (const modelKey of sequence) {
                const runner = providerExecutors[modelKey];
                if (!runner) continue;

                const isAvailable = providerAvailability[modelKey]?.();
                if (!isAvailable) {
                    const missingMessage = PROVIDER_HINTS[modelKey] || 'Configuration manquante (clé API ou proxy).';
                    lastErr = new Error(`${MODEL_LABELS[modelKey]} indisponible: ${missingMessage}`);
                    updateProviderStatus(modelKey, 'missing', missingMessage);
                    if (autoFallbackEnabled) notifyFallback(modelKey, missingMessage);
                    else break;
                    continue;
                }

                try {
                    result = await runner();
                    updateProviderStatus(modelKey, 'ok');
                    break;
                } catch (err) {
                    lastErr = err;
                    const reason = err?.message || err;
                    const statusInfo = describeErrorStatus(modelKey, reason);
                    updateProviderStatus(modelKey, statusInfo.status, statusInfo.reason);
                    if (autoFallbackEnabled) {
                        notifyFallback(modelKey, reason, statusInfo);
                        continue;
                    } else {
                        break;
                    }
                }
            }

            if (!result) throw lastErr || new Error('Aucun service de génération d\'image disponible');

            setGeneratedImage({
                url: result.url,
                prompt: trimmedPrompt,
                revisedPrompt: result.revisedPrompt || trimmedPrompt,
                provider: result.provider,
                timestamp: new Date()
            });

            setConversation(prev => [...prev, {
                id: `temp-image-${Date.now()}`,
                role: 'system',
                content: `🎨 Image générée via ${result.provider}: "${trimmedPrompt}"`,
                timestamp: new Date(),
                imageUrl: result.url,
                revisedPrompt: result.revisedPrompt || trimmedPrompt
            }]);

            setShowImageModal(true);
        } catch (error) {
            setConversation(prev => [...prev, {
                id: `temp-error-${Date.now()}`,
                role: 'system',
                content: `❌ Impossible de générer une image (${error.message}).`,
                timestamp: new Date()
            }]);
        } finally {
            setGeneratingImage(false);
        }
    };
    



    // ------------------------- UI PRINCIPALE -------------------------
    return (
        <>
            <div
                className="claude-page-container"
                style={{
                    backgroundImage: `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundAttachment: 'fixed',
                    backgroundBlendMode: 'overlay',
                    backgroundColor: '#200211',
                    opacity: '0.9',
                    minHeight: '100vh',
                }}
            >
                <div className="claude-container">
                    <div className="claude-header">
                    <section style={{margin: '12px 0 16px'}} className="claude-stream-box">
                      <ClaudeTester />
                    </section>
                        <h1 className="claude-title">
                            <span className="title-icon">🤖</span>
                            <span className="title-text">CLAUDE API</span>
                            <span className="title-glow">TEST</span>
                        </h1>

                        <div className="claude-controls">
                            <button
                                className="home-button"
                                onClick={() => navigate('/')}
                                title="Retour à l'accueil"
                            >
                                <span className="button-icon">🏠</span>
                                <span className="button-text">Accueil</span>
                            </button>

                            <button
                                className="upload-button"
                                onClick={() => fileInputRef.current?.click()}
                                title="Uploader des fichiers"
                            >
                                <span className="button-icon">📤</span>
                                <span className="button-text">Upload</span>
                            </button>

                            {(isDalleAvailable() || isStableDiffusionAvailable()) && (
                                <button
                                    className="generate-image-button"
                                    onClick={() => setShowImageModal(true)}
                                    title="Générer une image"
                                >
                                    <span className="button-icon">🎨</span>
                                    <span className="button-text">Générer Image</span>
                                </button>
                            )}

                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="image/*,.pdf,.txt,.doc,.docx"
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                            />

                            <span className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
                                {isConnected ? '🟢 Connecté' : '🔴 Déconnecté'}
                            </span>
                            <span
                                className="connection-status"
                                title={`Source API utilisée: ${apiSourceTooltip}`}
                                style={{ borderColor: '#00FFFF', color: '#00FFFF' }}
                            >
                                🔌 {`Source: ${apiSourceLabel}`}
                            </span>

                            <button
                                className="clear-button"
                                onClick={() => setShowClearConfirm(true)}
                                disabled={conversation.length === 0}
                            >
                                <span className="button-icon">🗑️</span>
                                <span className="button-text">Effacer mémoire</span>
                            </button>
                        </div>
                    </div>

                    {/* Zone des fichiers uploadés + contrôles */}
                    <div className="uploaded-files-container">
                            <h3 className="files-title">📁 Fichiers attachés ({uploadedFiles.length}/5)</h3>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
                                <button
                                    type="button"
                                    onClick={() => setIncludeFilesNextSend(v => !v)}
                                    title={`Inclure au prochain envoi${includeFilesNextSend ? ' (activé)' : ' (désactivé)'} \nRaccourci: Ctrl+I`}
                                    aria-label="Inclure au prochain envoi"
                                    style={{
                                        padding: '6px 10px',
                                        borderRadius: 8,
                                        background: includeFilesNextSend ? 'rgba(0,255,255,0.15)' : 'rgba(255,255,255,0.08)',
                                        border: includeFilesNextSend ? '1px solid #00FFFF' : '1px solid rgba(255,255,255,0.15)',
                                        color: includeFilesNextSend ? '#00FFFF' : '#FFFFFF',
                                        cursor: 'pointer'
                                    }}
                                >
                                    📎
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setKeepFilesAfterSend(v => !v)}
                                    title={`Conserver après envoi${keepFilesAfterSend ? ' (activé)' : ' (désactivé)'} \nRaccourci: Ctrl+K`}
                                    aria-label="Conserver après envoi"
                                    style={{
                                        padding: '6px 10px',
                                        borderRadius: 8,
                                        background: keepFilesAfterSend ? 'rgba(255,51,204,0.12)' : 'rgba(255,255,255,0.08)',
                                        border: keepFilesAfterSend ? '1px solid #FF33CC' : '1px solid rgba(255,255,255,0.15)',
                                        color: keepFilesAfterSend ? '#FF33CC' : '#FFFFFF',
                                        cursor: 'pointer'
                                    }}
                                >
                                    📌
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setUploadedFiles([])}
                                    title={`Effacer pièces jointes${uploadedFiles.length === 0 ? ' (aucune)' : ''} \nRaccourci: Ctrl+Backspace`}
                                    aria-label="Effacer pièces jointes"
                                    disabled={uploadedFiles.length === 0}
                                    style={{
                                        padding: '6px 10px',
                                        borderRadius: 8,
                                        background: 'rgba(255,255,255,0.08)',
                                        border: '1px solid rgba(255,255,255,0.15)',
                                        color: uploadedFiles.length === 0 ? 'rgba(255,255,255,0.4)' : '#FFFFFF',
                                        cursor: uploadedFiles.length === 0 ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    🗑️
                                </button>
                            </div>
                            {uploadedFiles.length > 0 && (
                              <div className="files-grid">
                                {uploadedFiles.map(file => (
                                    <div key={file.id} className="file-card">
                                        {file.preview ? (
                                            <div className="file-preview-container">
                                                <img 
                                                    src={file.preview} 
                                                    alt={file.name} 
                                                    className="file-preview"
                                                    onClick={() => {
                                                        setPreviewImage({
                                                            url: file.preview,
                                                            name: file.name
                                                        });
                                                    }}
                                                    title="Cliquer pour voir en grand"
                                                />
                                                <div className="preview-overlay">
                                                    <span className="preview-icon">🔍</span>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="file-icon">
                                                {file.type.includes('pdf') ? '📄' :
                                                    file.type.includes('doc') ? '📝' :
                                                        file.type.includes('text') ? '📃' : '📎'}
                                            </div>
                                        )}
                                        <div className="file-info">
                                            <p className="file-name">{file.name}</p>
                                            <p className="file-size">{(file.size / 1024).toFixed(1)} KB</p>
                                        </div>
                                        <button
                                            className="remove-file"
                                            onClick={() => removeFile(file.id)}
                                            title="Supprimer"
                                        >
                                            ✖
                                        </button>
                                    </div>
                                ))}
                              </div>
                            )}
                        </div>

                    <div className="claude-chat-container">
                        <div className="claude-messages">
                        {conversation.map((msg, i) => (
                                <div key={msg.id || i} className={`message-bubble ${msg.role}`}>
                                    <div className="message-header">
                                        <span className="message-author">
                                            {msg.role === 'user' ? '👤 Moi' : msg.role === 'claude' ? '🤖 Claude' : '💫 Système'}
                                        </span>
                                        <span className="message-time">
                                            {formatTimestamp(msg.timestamp)}
                                        </span>
                                    </div>
                                    <div className="message-content">
                                        {msg.content.length > 400 ? (
                                            <>
                                                <div className="preview-text" style={{ color: '#FFFFFF', fontSize: '1rem', lineHeight: '1.6' }}>{msg.content.slice(0, 300)}...</div>
                                                <button className="read-more-button" onClick={() => setModalMessage(msg.content)} style={{ marginTop: '0.5rem', padding: '0.4rem 0.8rem', background: 'rgba(0, 255, 255, 0.2)', border: '1px solid #00FFFF', borderRadius: '6px', color: '#00FFFF', cursor: 'pointer' }}>
                                                    🔍 Lire en grand
                                                </button>
                                            </>
                                        ) : (
                                            <div className="message-text" style={{ color: '#FFFFFF', fontSize: '1rem', lineHeight: '1.6' }}>{msg.content}</div>
                                        )}
                                        {msg.imageUrl && (
                                            <div className="generated-image-container">
                                                <img 
                                                    src={msg.imageUrl} 
                                                    alt="Générée" 
                                                    className="generated-image"
                                                    onClick={() => {
                                                        setPreviewImage({
                                                            url: msg.imageUrl,
                                                            name: "Image générée"
                                                        });
                                                    }}
                                                />
                                                {msg.revisedPrompt && (
                                                    <p className="revised-prompt">
                                                        <small>Prompt optimisé: {msg.revisedPrompt}</small>
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {/* Afficher les fichiers attachés pour les messages utilisateur */}
                                    {msg.role === 'user' && msg.attachedFiles && msg.attachedFiles.length > 0 && (
                                        <div className="user-attached-files" style={{ marginTop: '0.5rem' }}>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                                {msg.attachedFiles.filter(file => file.preview).map((file, index) => (
                                                    <img 
                                                        key={file.id || index}
                                                        src={file.preview} 
                                                        alt={file.name}
                                                        className="message-uploaded-image"
                                                        onClick={() => {
                                                            setPreviewImage({
                                                                url: file.preview,
                                                                name: file.name
                                                            });
                                                        }}
                                                        style={{
                                                            maxWidth: '150px',
                                                            maxHeight: '150px',
                                                            borderRadius: '8px',
                                                            border: '2px solid rgba(255, 51, 204, 0.5)',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            objectFit: 'cover'
                                                        }}
                                                        onMouseOver={(e) => {
                                                            e.target.style.border = '2px solid #FF33CC';
                                                            e.target.style.transform = 'scale(1.05)';
                                                            e.target.style.boxShadow = '0 0 20px rgba(255, 51, 204, 0.6)';
                                                        }}
                                                        onMouseOut={(e) => {
                                                            e.target.style.border = '2px solid rgba(255, 51, 204, 0.5)';
                                                            e.target.style.transform = 'scale(1)';
                                                            e.target.style.boxShadow = 'none';
                                                        }}
                                                        title={file.name}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {msg.role === 'system' && msg.content.includes('Fichiers ajoutés:') && (
                                        <div className="system-file-info" style={{ marginTop: '0.5rem' }}>
                                            {(() => {
                                                // Utiliser les fichiers attachés au message
                                                const filesSource = msg.attachedFiles || [];
                                                // Si pas de fichiers attachés, utiliser les fichiers actuellement uploadés
                                                if (filesSource.length === 0 && uploadedFiles.length > 0) {
                                                    filesSource.push(...uploadedFiles);
                                                }
                                                
                                                return (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                        {filesSource.map((file, index) => (
                                                            <div key={file.id || index} style={{ marginTop: index > 0 ? '0.5rem' : '0' }}>
                                                                {file.preview && (
                                                                    <div className="file-preview-container" style={{ marginTop: '0.5rem' }}>
                                                                        <img 
                                                                            src={file.preview} 
                                                                            alt={file.name}
                                                                            className="message-uploaded-image"
                                                                            onClick={() => {
                                                                                setPreviewImage({
                                                                                    url: file.preview,
                                                                                    name: file.name
                                                                                });
                                                                            }}
                                                                            style={{
                                                                                maxWidth: '200px',
                                                                                maxHeight: '200px',
                                                                                borderRadius: '8px',
                                                                                border: '2px solid rgba(0, 255, 255, 0.5)',
                                                                                cursor: 'pointer',
                                                                                transition: 'all 0.3s ease',
                                                                                objectFit: 'cover',
                                                                                display: 'block'
                                                                            }}
                                                                            onMouseOver={(e) => {
                                                                                e.target.style.border = '2px solid #00FFFF';
                                                                                e.target.style.transform = 'scale(1.05)';
                                                                                e.target.style.boxShadow = '0 0 20px rgba(0, 255, 255, 0.6)';
                                                                            }}
                                                                            onMouseOut={(e) => {
                                                                                e.target.style.border = '2px solid rgba(0, 255, 255, 0.5)';
                                                                                e.target.style.transform = 'scale(1)';
                                                                                e.target.style.boxShadow = 'none';
                                                                            }}
                                                                            title="Cliquer pour voir en grand"
                                                                        />
                                                                    </div>
                                                                )}
                                                                <span 
                                                                    className="file-name-link"
                                                                    onClick={() => {
                                                                        if (file && file.preview) {
                                                                            setPreviewImage({
                                                                                url: file.preview,
                                                                                name: file.name
                                                                            });
                                                                        }
                                                                    }}
                                                                    style={{
                                                                        color: '#00FFFF',
                                                                        cursor: file && file.preview ? 'pointer' : 'default',
                                                                        textDecoration: 'underline',
                                                                        fontSize: '0.9rem',
                                                                        display: 'inline-block',
                                                                        marginTop: '0.3rem',
                                                                        transition: 'all 0.2s ease'
                                                                    }}
                                                                    onMouseOver={(e) => {
                                                                        if (file && file.preview) {
                                                                            e.target.style.color = '#FF33CC';
                                                                            e.target.style.textShadow = '0 0 10px rgba(0, 255, 255, 0.8)';
                                                                        }
                                                                    }}
                                                                    onMouseOut={(e) => {
                                                                        e.target.style.color = '#00FFFF';
                                                                        e.target.style.textShadow = 'none';
                                                                    }}
                                                                    title={file && file.preview ? "Cliquer pour voir l'image" : ""}
                                                                >
                                                                    {file.name}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            ))}
                            {showTypingIndicator && (
                                <div className="message-bubble claude loading">
                                    <div className="typing-indicator">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </div>
                                </div>
                            )}

                            <div ref={messagesEndRef} />
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="claude-form">
                        <div className="input-container">
                            <textarea
                                className="claude-input"
                                value={userMessage}
                                onChange={(e) => setUserMessage(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSubmit(e);
                                    }
                                }}
                                placeholder="Tapez votre message pour Claude... (Entrée pour envoyer, Shift+Entrée pour nouvelle ligne)"
                                rows="5"
                                disabled={!isConnected}
                            />
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button
                                        type="submit"
                                        className="claude-button"
                                        style={{ background: 'linear-gradient(90deg, #00FFFF66, #FF33CC66)' }}
                                        disabled={chatBusy || !userMessage.trim() || !isConnected}
                                        title="Envoi normal (réponse d'un coup)"
                                    >
                                        <span className="button-text">Envoyer</span>
                                        <span className="button-icon">➤</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="claude-button"
                                        style={{ background: 'linear-gradient(90deg, #FF33CC66, #00FFFF66)' }}
                                        disabled={chatBusy || !userMessage.trim() || !isConnected}
                                        title="Envoi en streaming (réponse en direct)"
                                        onClick={handleSubmitStream}
                                        >
                                        <span className="button-text">Streamer</span>
                                        <span className="button-icon">⚡</span>
                                    </button>
                                    {isStreaming && (
                                        <button
                                            type="button"
                                            className="claude-button"
                                            style={{ background: 'rgba(255, 0, 0, 0.35)' }}
                                            onClick={handleStopStreaming}
                                            title="Arrêter le streaming en cours"
                                        >
                                            <span className="button-text">Stop</span>
                                            <span className="button-icon">⏹️</span>
                                        </button>
                                    )}
                                </div>
                        </div>
                    </form>

                    {/* Modal de confirmation pour effacer */}
                    {showClearConfirm && (
                        <div className="clear-confirm-modal">
                            <div className="modal-content">
                                <h3>⚠️ Confirmer la suppression</h3>
                                <p>Êtes-vous sûr de vouloir effacer tout l'historique de conversation ?</p>
                                <div className="modal-buttons">
                                    <button
                                        className="confirm-button"
                                        onClick={clearHistory}
                                    >
                                        Oui, effacer
                                    </button>
                                    <button
                                        className="cancel-button"
                                        onClick={() => setShowClearConfirm(false)}
                                    >
                                        Annuler
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {modalMessage && (
                <ClaudeMessageModal
                    content={modalMessage}
                    onClose={() => setModalMessage(null)}
                />
            )}

            {/* Modal de génération d'image */}
            {showImageModal && (
                <div className="image-generation-modal">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>🎨 Génération d'Image DALL-E</h3>
                            <button 
                                className="close-button"
                                onClick={() => {
                                    setShowImageModal(false);
                                    setImagePrompt('');
                                    setGeneratedImage(null);
                                }}
                            >
                                ✖
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <div className="prompt-section">
                                <label htmlFor="image-prompt">Description de l'image à générer :</label>
                                <textarea
                                    id="image-prompt"
                                    value={imagePrompt}
                                    onChange={(e) => setImagePrompt(e.target.value)}
                                    placeholder="Décrivez l'image que vous voulez générer... (ex: 'Une affiche de film cyberpunk avec des néons bleus')"
                                    rows="4"
                                    disabled={generatingImage}
                                />
                            </div>
                            <div className="chat-list">
                                {chat.map((m, i) => (
                                    <div key={i} className={`msg ${m.who}`}>
                                    <pre>{m.text}</pre>
                                    </div>
                                ))}
                            </div>
                            {/* Affichage de l'image générée */}
                            {generatedImage && (
                                <div className="generated-image-container">
                                    <img 
                                        src={generatedImage.url} 
                                        alt="Générée" 
                                        className="generated-image"
                                        onClick={() => {
                                            setPreviewImage({
                                                url: generatedImage.url,
                                                name: "Image générée"
                                            });
                                        }}
                                        style={{ cursor: 'pointer' }} // Ajoutez ceci
                                    />
                                    {generatedImage.provider && (
                                        <p className="revised-prompt" style={{ marginTop: '0.25rem' }}>
                                            <small>Source: {generatedImage.provider}</small>
                                        </p>
                                    )}
                                    {generatedImage.revisedPrompt && (
                                        <p className="revised-prompt">
                                            <small>Prompt optimisé: {generatedImage.revisedPrompt}</small>
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className="generation-options">
                                <div className="model-selection">
                                    {MODEL_OPTION_DEFINITIONS.map((option) => {
                                        const statusInfo = providerStatus[option.key];
                                        const available = providerAvailability[option.key]?.();
                                        const disabled = !available || ['blocked', 'missing'].includes(statusInfo?.status);
                                        const warnMessage = statusInfo?.reason || (!available ? PROVIDER_HINTS[option.key] : null);
                                        return (
                                            <label
                                                key={option.key}
                                                className={`model-option ${selectedModel === option.key ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                                            >
                                                <div className="model-option-row">
                                                    <input
                                                        type="radio"
                                                        name="model"
                                                        value={option.key}
                                                        checked={selectedModel === option.key}
                                                        onChange={() => setSelectedModel(option.key)}
                                                        disabled={disabled}
                                                    />
                                                    <div className="model-option-text">
                                                        <span className="model-option-title">{option.title}</span>
                                                        <small>{option.description}</small>
                                                    </div>
                                                </div>
                                                {warnMessage && (
                                                    <small className="model-option-warning">{warnMessage}</small>
                                                )}
                                            </label>
                                        );
                                    })}
                                </div>
                                <div className="fallback-row">
                                    <button
                                        type="button"
                                        title={`Fallback auto ${autoFallbackEnabled ? 'activé' : 'désactivé'}`}
                                        aria-label="Activer/désactiver fallback automatique"
                                        onClick={() => setAutoFallbackEnabled(v => !v)}
                                        style={{
                                            padding: '6px 10px',
                                            borderRadius: 8,
                                            background: autoFallbackEnabled ? 'rgba(0,255,255,0.15)' : 'rgba(255,255,255,0.08)',
                                            border: autoFallbackEnabled ? '1px solid #00FFFF' : '1px solid rgba(255,255,255,0.15)',
                                            color: autoFallbackEnabled ? '#00FFFF' : '#FFFFFF',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        🔁 Fallback
                                    </button>
                                    <span className="fallback-hint">
                                        Essaie automatiquement les autres services si celui choisi échoue.
                                    </span>
                                </div>
                                <div className="ratio-section">
                                    <p className="ratio-title">Format / ratio :</p>
                                    <div className="ratio-options">
                                        {ASPECT_RATIOS.map((ratio) => (
                                            <button
                                                key={ratio.key}
                                                type="button"
                                                className={`ratio-chip ${selectedRatio === ratio.key ? 'active' : ''}`}
                                                onClick={() => setSelectedRatio(ratio.key)}
                                            >
                                                <span>{ratio.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                    <p className="ratio-hint">
                                        Appliqué à DALL·E 3 et Stable Diffusion (dimensions adaptées). Grok et DALL·E 2 restent en 1024×1024.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="generate-button"
                                onClick={() => handleGenerateImage(imagePrompt, selectedModel)}
                                disabled={!imagePrompt.trim() || generatingImage}
                            >
                                {generatingImage ? '🎨 Génération en cours...' : '🎨 Générer l\'Image'}
                            </button>
                            
                            <button
                                className="cancel-button"
                                onClick={() => {
                                    setShowImageModal(false);
                                    setImagePrompt('');
                                    setGeneratedImage(null);
                                }}
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {previewImage && (
    <div 
        className="image-preview-overlay"
        onClick={() => setPreviewImage(null)}
        style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3000,
            cursor: 'pointer',
            backdropFilter: 'blur(10px)'
        }}
    >
        <div 
            className="image-preview-content"
            onClick={(e) => e.stopPropagation()}
            style={{
                position: 'relative',
                maxWidth: '90vw',
                maxHeight: '90vh'
            }}
        >
            <button
                className="preview-close-btn"
                onClick={() => setPreviewImage(null)}
                style={{
                    position: 'absolute',
                    top: '-40px',
                    right: '-40px',
                    background: '#D90093',
                    border: 'none',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    color: 'white',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10
                }}
            >
                ✖
            </button>
            <img 
                src={previewImage.url} 
                alt={previewImage.name}
                style={{
                    maxWidth: '100%',
                    maxHeight: '85vh',
                    objectFit: 'contain',
                    borderRadius: '12px',
                    boxShadow: '0 0 50px rgba(0, 255, 255, 0.5), 0 0 20px rgba(255, 51, 204, 0.5)'
                }}
            />
            <p style={{
                textAlign: 'center',
                color: 'white',
                marginTop: '1rem',
                fontSize: '1.1rem',
                padding: '0.5rem 1rem',
                background: 'rgba(0, 0, 0, 0.7)',
                borderRadius: '8px',
                border: '1px solid rgba(0, 255, 255, 0.3)'
            }}>
                {previewImage.name}
            </p>
        </div>
    </div>
)}
        </>
    );
}




// import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
// import { UserContext } from '../UserContext';
// import { askClaude, saveClaudeMessage } from '../utils/claudeApi';
// import { db } from '../config/firebasedeconfig';
// import {
//     collection,
//     query,
//     orderBy,
//     onSnapshot,
//     deleteDoc,
//     getDocs,
//     limit,
//     startAfter,
//     serverTimestamp
// } from 'firebase/firestore';
// import { useNavigate } from 'react-router-dom';
// import '../styles/components/ClaudeTest.scss';
// import ClaudeMessageModal from './ClaudeMessageModal';



// export default function ClaudeTest() {
//     const { user } = useContext(UserContext);
//     const navigate = useNavigate();
//     const [userMessage, setUserMessage] = useState('');
//     const [conversation, setConversation] = useState([]);
//     const [loading, setLoading] = useState(false);
//     const [isConnected, setIsConnected] = useState(true);
//     const [showClearConfirm, setShowClearConfirm] = useState(false);
//     const [loadingMore, setLoadingMore] = useState(false);
//     const [hasMore, setHasMore] = useState(false);
//     const [uploadedFiles, setUploadedFiles] = useState([]);
//     const messagesEndRef = useRef(null);
//     const lastDocRef = useRef(null);
//     const fileInputRef = useRef(null);
//     const [modalMessage, setModalMessage] = useState(null);

//     // Cache local pour améliorer les performances
//     const conversationCache = useRef(new Map());

//     // Fonction pour faire défiler jusqu'au dernier message
//     const scrollToBottom = () => {
//         messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
//     };

//     useEffect(() => {
//         scrollToBottom();
//     }, [conversation]);

//     // Optimisation avec cache et gestion d'erreurs améliorée
//     useEffect(() => {
//         if (!user?.uid) return;

//         const loadMessages = async () => {
//             try {
//                 setIsConnected(true);

//                 // TEMPORAIREMENT DÉSACTIVÉ POUR TESTER CLAUDE
//                 console.log('⚠️ Historique Firestore temporairement désactivé pour les tests');
//                 setConversation([]);

//                 /* COMMENTÉ TEMPORAIREMENT
//                 // Charger depuis le cache d'abord
//                 const cachedMessages = conversationCache.current.get(user.uid);
//                 if (cachedMessages) {
//                   setConversation(cachedMessages);
//                 }
        
//                 const q = query(
//                   collection(db, 'claudeMessages', user.uid, 'messages'),
//                   orderBy('timestamp', 'desc'),
//                   limit(20) // Charger seulement les 20 derniers messages initialement
//                 );
        
//                 const unsubscribe = onSnapshot(
//                   q, 
//                   (snapshot) => {
//                     const messages = snapshot.docs
//                       .map((doc) => ({
//                         id: doc.id,
//                         ...doc.data(),
//                         timestamp: doc.data().timestamp?.toDate?.() || new Date()
//                       }))
//                       .reverse(); // Inverser pour avoir l'ordre chronologique
                    
//                     setConversation(messages);
//                     conversationCache.current.set(user.uid, messages);
                    
//                     // Vérifier s'il y a plus de messages
//                     setHasMore(snapshot.docs.length === 20);
//                     if (snapshot.docs.length > 0) {
//                       lastDocRef.current = snapshot.docs[0];
//                     }
//                   },
//                   (error) => {
//                     console.error('Erreur Firestore:', error);
//                     setIsConnected(false);
//                   }
//                 );
        
//                 return () => unsubscribe();
//                 */
//             } catch (error) {
//                 console.error('Erreur lors du chargement des messages:', error);
//                 setIsConnected(false);
//             }
//         };

//         loadMessages();
//     }, [user]);

//     // Charger plus de messages (pagination)
//     const loadMoreMessages = useCallback(async () => {
//         if (!user?.uid || !lastDocRef.current || loadingMore) return;

//         setLoadingMore(true);
//         try {
//             const q = query(
//                 collection(db, 'claudeMessages', user.uid, 'messages'),
//                 orderBy('timestamp', 'desc'),
//                 startAfter(lastDocRef.current),
//                 limit(20)
//             );

//             const snapshot = await getDocs(q);
//             const olderMessages = snapshot.docs
//                 .map((doc) => ({
//                     id: doc.id,
//                     ...doc.data(),
//                     timestamp: doc.data().timestamp?.toDate?.() || new Date()
//                 }))
//                 .reverse();

//             if (olderMessages.length > 0) {
//                 setConversation(prev => [...olderMessages, ...prev]);
//                 lastDocRef.current = snapshot.docs[snapshot.docs.length - 1];
//                 setHasMore(snapshot.docs.length === 20);
//             } else {
//                 setHasMore(false);
//             }
//         } catch (error) {
//             console.error('Erreur lors du chargement des anciens messages:', error);
//         } finally {
//             setLoadingMore(false);
//         }
//     }, [user, loadingMore]);

//     // Fonction pour redimensionner l'image
//     const resizeImage = (file, maxWidth = 1024, maxHeight = 1024) => {
//         return new Promise((resolve) => {
//             const reader = new FileReader();
//             reader.onload = (e) => {
//                 const img = new Image();
//                 img.onload = () => {
//                     const canvas = document.createElement('canvas');
//                     let width = img.width;
//                     let height = img.height;

//                     // Calculer les nouvelles dimensions
//                     if (width > height) {
//                         if (width > maxWidth) {
//                             height = Math.round((height *= maxWidth / width));
//                             width = maxWidth;
//                         }
//                     } else {
//                         if (height > maxHeight) {
//                             width = Math.round((width *= maxHeight / height));
//                             height = maxHeight;
//                         }
//                     }

//                     canvas.width = width;
//                     canvas.height = height;

//                     const ctx = canvas.getContext('2d');
//                     ctx.drawImage(img, 0, 0, width, height);

//                     // Convertir en base64 avec compression
//                     const resizedBase64 = canvas.toDataURL(file.type, 0.8);
//                     resolve(resizedBase64);
//                 };
//                 img.src = e.target.result;
//             };
//             reader.readAsDataURL(file);
//         });
//     };

//     // Gestion de l'upload de fichiers
//     const handleFileUpload = async (e) => {
//         const files = Array.from(e.target.files);
//         if (files.length === 0) return;

//         // Limite à 5 fichiers maximum
//         const maxFiles = 5;
//         const filesToUpload = files.slice(0, maxFiles);

//         // Convertir les fichiers en base64 pour l'aperçu
//         const filePromises = filesToUpload.map(async (file) => {
//             let data = null;
//             let preview = null;

//             if (file.type.startsWith('image/')) {
//                 // Redimensionner l'image si nécessaire
//                 data = await resizeImage(file);
//                 preview = data;
//             } else {
//                 // Pour les autres fichiers, lire normalement
//                 data = await new Promise((resolve) => {
//                     const reader = new FileReader();
//                     reader.onloadend = () => resolve(reader.result);
//                     reader.readAsDataURL(file);
//                 });
//             }

//             return {
//                 id: `file-${Date.now()}-${Math.random()}`,
//                 name: file.name,
//                 type: file.type,
//                 size: file.size,
//                 data: data,
//                 preview: preview
//             };
//         });

//         Promise.all(filePromises).then(uploadedFilesData => {
//             setUploadedFiles(prev => [...prev, ...uploadedFilesData]);

//             // Message de confirmation
//             const fileNames = uploadedFilesData.map(f => f.name).join(', ');
//             const tempMessage = {
//                 id: 'temp-upload',
//                 role: 'system',
//                 content: `📎 Fichiers ajoutés: ${fileNames}`,
//                 timestamp: new Date()
//             };
//             setConversation(prev => [...prev, tempMessage]);
//         });
//     };

//     // Supprimer un fichier uploadé
//     const removeFile = (fileId) => {
//         setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
//     };

//     // Effacer l'historique
//     const clearHistory = async () => {
//         if (!user?.uid) return;

//         try {
//             const messagesRef = collection(db, 'claudeMessages', user.uid, 'messages');
//             const snapshot = await getDocs(messagesRef);

//             // Supprimer tous les documents
//             const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
//             await Promise.all(deletePromises);

//             // Vider le cache et l'état local
//             conversationCache.current.delete(user.uid);
//             setConversation([]);
//             setShowClearConfirm(false);

//             // Message de confirmation visuel
//             const tempMessage = {
//                 id: 'temp-clear',
//                 role: 'system',
//                 content: '💫 Historique effacé avec succès',
//                 timestamp: new Date()
//             };
//             setConversation([tempMessage]);

//             // Retirer le message temporaire après 2 secondes
//             setTimeout(() => {
//                 setConversation([]);
//             }, 2000);

//         } catch (error) {
//             console.error('Erreur lors de la suppression de l\'historique:', error);
//         }
//     };

//     const handleSubmit = async (e) => {
//         e.preventDefault();
//         if (!userMessage.trim() || loading) return;

//         const trimmed = userMessage.trim();
//         console.log('🚀 Envoi du message:', trimmed);
//         setLoading(true);
//         setUserMessage('');

//         // Ajouter le message utilisateur immédiatement
//         const tempUserMsg = {
//             id: `temp-user-${Date.now()}`,
//             role: 'user',
//             content: trimmed,
//             timestamp: new Date()
//         };
//         setConversation(prev => [...prev, tempUserMsg]);

//         try {
//             // TESTER UNIQUEMENT L'API CLAUDE
//             console.log('🤖 Appel de Claude API...');

//             // Préparer les images pour l'envoi (seulement les images)
//             const imagesToSend = uploadedFiles.filter(file => file.type.startsWith('image/'));

//             const recentHistory = conversation
//                 .filter(msg => msg.role === 'user' || msg.role === 'claude')
//                 .slice(-5);


//             // Envoyer le message avec les images
//             const responseText = await askClaude(trimmed, imagesToSend);
//             console.log('✅ Réponse reçue de Claude:', responseText);

//             // Ajouter la réponse de Claude à la conversation
//             const claudeMsg = {
//                 id: `temp-claude-${Date.now()}`,
//                 role: 'claude',
//                 content: responseText,
//                 timestamp: new Date()
//             };
//             setConversation(prev => [...prev, claudeMsg]);

//             // Effacer les fichiers après envoi
//             setUploadedFiles([]);

//             // Sauvegarde Firestore désactivée temporairement
//             console.log('⚠️ Sauvegarde Firestore désactivée pour les tests');


//         } catch (error) {
//             console.error('❌ Erreur API Claude:', error);

//             // Ajouter un message d'erreur
//             const errorMsg = {
//                 id: `temp-error-${Date.now()}`,
//                 role: 'system',
//                 content: `❌ Erreur: ${error.message}`,
//                 timestamp: new Date()
//             };
//             setConversation(prev => [...prev, errorMsg]);
//         } finally {
//             setLoading(false);
//         }
//     };

//     // Formater l'horodatage
//     const formatTimestamp = (timestamp) => {
//         if (!timestamp) return '';
//         const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
//         return date.toLocaleTimeString('fr-FR', {
//             hour: '2-digit',
//             minute: '2-digit'
//         });
//     };

//     return (
//         <>
//             <div
//                 className="claude-page-container"
//                 style={{
//                     backgroundImage: `url(${process.env.PUBLIC_URL}/images/darkwater.jpg)`,
//                     backgroundSize: 'cover',
//                     backgroundPosition: 'center',
//                     backgroundRepeat: 'no-repeat',
//                     backgroundAttachment: 'fixed',
//                     backgroundBlendMode: 'overlay',
//                     backgroundColor: '#200211',
//                     opacity: '0.9',
//                     overflowX: 'hidden',
//                     overflowY: 'auto',
//                     minHeight: '100vh',
//                 }}
//             >
//                 <div className="claude-container">
//                     <div className="claude-header">
//                         <h1 className="claude-title">
//                             <span className="title-icon">🤖</span>
//                             <span className="title-text">CLAUDE API</span>
//                             <span className="title-glow">TEST</span>
//                         </h1>

//                         <div className="claude-controls">
//                             <button
//                                 className="home-button"
//                                 onClick={() => navigate('/')}
//                                 title="Retour à l'accueil"
//                             >
//                                 <span className="button-icon">🏠</span>
//                                 <span className="button-text">Accueil</span>
//                             </button>

//                             <button
//                                 className="upload-button"
//                                 onClick={() => fileInputRef.current?.click()}
//                                 title="Uploader des fichiers"
//                             >
//                                 <span className="button-icon">📤</span>
//                                 <span className="button-text">Upload</span>
//                             </button>

//                             <input
//                                 ref={fileInputRef}
//                                 type="file"
//                                 multiple
//                                 accept="image/*,.pdf,.txt,.doc,.docx"
//                                 onChange={handleFileUpload}
//                                 style={{ display: 'none' }}
//                             />

//                             <span className={`connection-status ${isConnected ? 'connected' : 'disconnected'}`}>
//                                 {isConnected ? '🟢 Connecté' : '🔴 Déconnecté'}
//                             </span>

//                             <button
//                                 className="clear-button"
//                                 onClick={() => setShowClearConfirm(true)}
//                                 disabled={conversation.length === 0}
//                             >
//                                 <span className="button-icon">🗑️</span>
//                                 <span className="button-text">Effacer mémoire</span>
//                             </button>
//                         </div>
//                     </div>

//                     {/* Zone des fichiers uploadés */}
//                     {uploadedFiles.length > 0 && (
//                         <div className="uploaded-files-container">
//                             <h3 className="files-title">📁 Fichiers attachés ({uploadedFiles.length}/5)</h3>
//                             <div className="files-grid">
//                                 {uploadedFiles.map(file => (
//                                     <div key={file.id} className="file-card">
//                                         {file.preview ? (
//                                             <img src={file.preview} alt={file.name} className="file-preview" />
//                                         ) : (
//                                             <div className="file-icon">
//                                                 {file.type.includes('pdf') ? '📄' :
//                                                     file.type.includes('doc') ? '📝' :
//                                                         file.type.includes('text') ? '📃' : '📎'}
//                                             </div>
//                                         )}
//                                         <div className="file-info">
//                                             <p className="file-name">{file.name}</p>
//                                             <p className="file-size">{(file.size / 1024).toFixed(1)} KB</p>
//                                         </div>
//                                         <button
//                                             className="remove-file"
//                                             onClick={() => removeFile(file.id)}
//                                             title="Supprimer"
//                                         >
//                                             ✖
//                                         </button>
//                                     </div>
//                                 ))}
//                             </div>
//                         </div>
//                     )}

//                     <div className="claude-chat-container">
//                         <div className="claude-messages">
//                             {hasMore && (
//                                 <button
//                                     className="load-more-button"
//                                     onClick={loadMoreMessages}
//                                     disabled={loadingMore}
//                                 >
//                                     {loadingMore ? '⏳ Chargement...' : '📜 Charger plus de messages'}
//                                 </button>
//                             )}

//                             {conversation.map((msg, i) => (
//                                 <div key={msg.id || i} className={`message-bubble ${msg.role}`}>
//                                     <div className="message-header">
//                                         <span className="message-author">
//                                             {msg.role === 'user' ? '👤 Moi' : msg.role === 'claude' ? '🤖 Claude' : '💫 Système'}
//                                         </span>
//                                         <span className="message-time">
//                                             {formatTimestamp(msg.timestamp)}
//                                         </span>
//                                     </div>
//                                     <div className="message-content">
//                                         {msg.content.length > 400 ? (
//                                             <>
//                                                 <div className="preview-text">{msg.content.slice(0, 300)}...</div>
//                                                 <button className="read-more-button" onClick={() => setModalMessage(msg.content)}>
//                                                     🔍 Lire en grand
//                                                 </button>
//                                             </>
//                                         ) : (
//                                             msg.content
//                                         )}
//                                     </div>
//                                 </div>
//                             ))}

//                             {loading && (
//                                 <div className="message-bubble claude loading">
//                                     <div className="typing-indicator">
//                                         <span></span>
//                                         <span></span>
//                                         <span></span>
//                                     </div>
//                                 </div>
//                             )}

//                             <div ref={messagesEndRef} />
//                         </div>
//                     </div>

//                     <form onSubmit={handleSubmit} className="claude-form">
//                         <div className="input-container">
//                             <textarea
//                                 className="claude-input"
//                                 value={userMessage}
//                                 onChange={(e) => setUserMessage(e.target.value)}
//                                 onKeyPress={(e) => {
//                                     if (e.key === 'Enter' && !e.shiftKey) {
//                                         e.preventDefault();
//                                         handleSubmit(e);
//                                     }
//                                 }}
//                                 placeholder="Tapez votre message pour Claude... (Entrée pour envoyer, Shift+Entrée pour nouvelle ligne)"
//                                 rows="5"
//                                 disabled={!isConnected}
//                             />
//                             <button
//                                 type="submit"
//                                 className="claude-button"
//                                 disabled={loading || !userMessage.trim() || !isConnected}
//                             >
//                                 <span className="button-text">Envoyer</span>
//                                 <span className="button-icon">➤</span>
//                             </button>
//                         </div>
//                     </form>

//                     {/* Modal de confirmation pour effacer */}
//                     {showClearConfirm && (
//                         <div className="clear-confirm-modal">
//                             <div className="modal-content">
//                                 <h3>⚠️ Confirmer la suppression</h3>
//                                 <p>Êtes-vous sûr de vouloir effacer tout l'historique de conversation ?</p>
//                                 <div className="modal-buttons">
//                                     <button
//                                         className="confirm-button"
//                                         onClick={clearHistory}
//                                     >
//                                         Oui, effacer
//                                     </button>
//                                     <button
//                                         className="cancel-button"
//                                         onClick={() => setShowClearConfirm(false)}
//                                     >
//                                         Annuler
//                                     </button>
//                                 </div>
//                             </div>
//                         </div>
//                     )}
//                 </div>
//             </div>
//             {modalMessage && (
//                 <ClaudeMessageModal
//                     content={modalMessage}
//                     onClose={() => setModalMessage(null)}
//                 />
//             )}
//         </>
//     );
// }
